import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';

import './date-interval-picker.scss';

interface DateIntervalPickerProps {
	startDate?: Date;
	endDate?: Date;
	onChange?: (startDate: Date | null, endDate: Date | null) => void;
	minDate?: Date;
	maxDate?: Date;
	maxDuration?: number;
	placeholder?: string;
	disabled?: boolean;
	className?: string;
	error?: boolean;
	withTime?: boolean;
}

const DateIntervalPicker: React.FC<DateIntervalPickerProps> = ({
	startDate,
	endDate,
	onChange,
	maxDuration = 365,
	placeholder = 'Выбрать период',
	disabled = false,
	className = '',
	error = false,
	withTime = false,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [start, setStart] = useState<Date | null>(null);
	const [end, setEnd] = useState<Date | null>(null);
	const [duration, setDuration] = useState(0);
	const [startInputValue, setStartInputValue] = useState('');
	const [endInputValue, setEndInputValue] = useState('');
	const pickerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (startDate) {
			const normalizedStart = withTime ? moment(startDate).toDate() : moment(startDate).startOf('day').toDate();
			setStart(normalizedStart);
			setStartInputValue(moment(normalizedStart).format(withTime ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD'));
		}

		if (endDate) {
			const normalizedEnd = withTime ? moment(endDate).toDate() : moment(endDate).startOf('day').toDate();
			setEnd(normalizedEnd);
			setEndInputValue(moment(normalizedEnd).format(withTime ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD'));
		}
	}, [startDate, endDate, withTime]);

	useEffect(() => {
		if (start && end) {
			const startMoment = moment(start);
			const endMoment = moment(end);
			const newDuration = endMoment.diff(startMoment, 'days') + 1;
			setDuration(Math.max(0, newDuration));
		} else {
			setDuration(0);
		}
	}, [start, end]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const isDateComplete = (dateString: string): boolean => {
		const datePattern = /^\d{4}-\d{2}-\d{2}$/;
		const dateTimePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;
		return withTime ? dateTimePattern.test(dateString) : datePattern.test(dateString);
	};

	const withDefaultTime = (value: string): string => {
		if (withTime && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
			return `${value}T00:00`;
		}
		return value;
	};

	const handleStartDateChange = (value: string) => {
		setStartInputValue(withDefaultTime(value));
	};

	const handleEndDateChange = (value: string) => {
		setEndInputValue(withDefaultTime(value));
	};

	const handleStartBlur = () => {
		const normalized = withDefaultTime(startInputValue);
		if (normalized !== startInputValue) {
			setStartInputValue(normalized);
		}
		if (normalized && !isDateComplete(normalized)) {
			setStartInputValue(start ? moment(start).format(withTime ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD') : '');
		}

		if (isDateComplete(normalized)) {
			const newDate = moment(normalized).toDate();

			if (moment(normalized).isValid() && newDate.getFullYear() >= 2000) {
				setStart(newDate);

				if (end && newDate > end) {
					setEnd(newDate);
					setEndInputValue(normalized);
				}
			}
		}
	};

	const handleEndBlur = () => {
		const normalized = withDefaultTime(endInputValue);
		if (normalized !== endInputValue) {
			setEndInputValue(normalized);
		}
		if (normalized && !isDateComplete(normalized)) {
			setEndInputValue(end ? moment(end).format(withTime ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD') : '');
		}

		if (isDateComplete(normalized)) {
			const newDate = moment(normalized).toDate();

			if (moment(normalized).isValid() && newDate.getFullYear() >= 2000) {
				setEnd(newDate);

				if (start && newDate < start) {
					setStart(newDate);
					setStartInputValue(normalized);
				}
			}
		}
	};

	const handleApply = () => {
		if (start && end) {
			onChange?.(start, withTime ? end : moment(end).endOf('day').toDate());
		} else {
			onChange?.(start, withTime ? end : moment(end).endOf('day').toDate());
		}
		setIsOpen(false);
	};

	const handleClear = () => {
		setStart(null);
		setEnd(null);
		setStartInputValue('');
		setEndInputValue('');
		onChange?.(null, null);
		setIsOpen(false);
	};

	const formatDate = (date: Date): string => {
		return date.toLocaleString(
			'ru-RU',
			withTime
				? {
						day: '2-digit',
						month: '2-digit',
						year: 'numeric',
						hour: '2-digit',
						minute: '2-digit',
					}
				: {
						day: '2-digit',
						month: '2-digit',
						year: 'numeric',
					},
		);
	};

	const formatDuration = (days: number): string => {
		if (days === 1) return '1 день';
		if (days >= 2 && days <= 4) return `${days} дня`;
		return `${days} дней`;
	};

	const containerClasses = [
		'date-interval-picker',
		className,
		isOpen && 'is-open',
		error && 'has-error',
		disabled && 'is-disabled',
	]
		.filter(Boolean)
		.join(' ');

	return (
		<div className={containerClasses} ref={pickerRef}>
			<div className="date-interval-picker__input" onClick={() => !disabled && setIsOpen(!isOpen)}>
				{startDate && endDate ? (
					<div className="date-interval-picker__display-multiline">
						<div className="date-interval-picker__date-line">
							<span> от</span>
							<span className="date-interval-picker__date-value">{formatDate(startDate)}</span>
						</div>
						<span style={{ height: '1px', lineHeight: '1px' }}></span>
						<div className="date-interval-picker__date-line">
							<span>до</span>
							<span className="date-interval-picker__date-value">{formatDate(endDate)}</span>
						</div>
					</div>
				) : (
					<div className="date-interval-picker__display-placeholder">{placeholder}</div>
				)}
			</div>

			{isOpen && (
				<div className="date-interval-picker__dropdown">
					<div className="date-interval-picker__header">
						<h3>Выбор интервала</h3>
						<div className="date-interval-picker__duration-info">
							{maxDuration && duration > maxDuration && (
								<span className="date-interval-picker__error-text">
									(максимум {formatDuration(maxDuration)})
								</span>
							)}
						</div>
					</div>

					<div className="date-interval-picker__date-section">
						<label className="date-interval-picker__date-label">Начальная дата</label>
						<div className="date-interval-picker__date-controls">
							<input
								type={withTime ? 'datetime-local' : 'date'}
								value={startInputValue}
								onChange={e => handleStartDateChange(e.target.value)}
								onBlur={handleStartBlur}
								className="date-interval-picker__date-input"
							/>
						</div>
					</div>

					<div className="date-interval-picker__date-section">
						<label className="date-interval-picker__date-label">Конечная дата</label>
						<div className="date-interval-picker__date-controls">
							<input
								type={withTime ? 'datetime-local' : 'date'}
								value={endInputValue}
								onChange={e => handleEndDateChange(e.target.value)}
								onBlur={handleEndBlur}
								className="date-interval-picker__date-input"
							/>
						</div>
					</div>

					<div className="date-interval-picker__actions">
						<button
							type="button"
							className="date-interval-picker__action-btn date-interval-picker__action-btn--clear"
							onClick={handleClear}
						>
							Очистить
						</button>
						<button
							type="button"
							className="button button--solid"
							onClick={handleApply}
							disabled={!start || !end}
						>
							Применить
						</button>
					</div>
				</div>
			)}
		</div>
	);
};

export default DateIntervalPicker;
